import { useFragment } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import { useEffect, useState } from 'react'
import { resume_useIsImportingResume$key } from '__generated__/resume_useIsImportingResume.graphql'
import { isPast } from 'services/timeKeeping'
import { resume_ResumeSubscription } from '__generated__/resume_ResumeSubscription.graphql'
import { useSubscription } from './relay'
import { useIsMutating } from './mutations'

export const CREATE_RESUME_ID_PLACEHOLDER = 'createResume-id-placeholder'

export function useIsImportingResume(resumeFragmentRef: resume_useIsImportingResume$key | null = null, onLoadingChange?: (loading: boolean) => void) {
  const resume = useFragment(graphql`
    fragment resume_useIsImportingResume on Resume {
      id
      status
      importTimeoutAt
      copyToProfile
      user {
        id
      }
    }
  `, resumeFragmentRef)

  useSubscription<resume_ResumeSubscription>({
    subscription: graphql`
      subscription resume_ResumeSubscription($userId: ID) {
        resumeProcessingFinished(userId: $userId) {
          resume {
            id
            ...resume_useIsImportingResume
            user {
              resume {
                id
                ...resume_useIsImportingResume
              }
              ...FullProfile_User # so that we refresh all profile data
            }
          }
        }
      }
    `,
    variables: {
      userId: resume?.user?.id,
    },
  })

  const [timedOut, setTimedOut] = useState(isPast(resume?.importTimeoutAt) || false)

  useEffect(() => {
    setTimedOut(isPast(resume?.importTimeoutAt) || false)

    let intervalId: number

    if (resume?.importTimeoutAt) {
      intervalId = window.setInterval(() => {
        setTimedOut(isPast(resume.importTimeoutAt) || false)
      }, 1000)
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [resume?.importTimeoutAt])

  const isCreateResumeExecuting = useIsMutating(CREATE_RESUME_ID_PLACEHOLDER)
  const unprocessed = resume?.copyToProfile && resume?.status && ['unprocessed', 'processing', 'processing_queued'].includes(resume?.status)
  const loading = isCreateResumeExecuting || (unprocessed && !timedOut) || false

  const [isCurrentlyLoading, setIsCurrentlyLoading] = useState(loading)

  useEffect(() => {
    if (loading !== isCurrentlyLoading) {
      setIsCurrentlyLoading(loading)
      onLoadingChange?.(loading)
    }
  }, [loading, isCurrentlyLoading, onLoadingChange])

  return {
    id: resume?.id,
    loading: isCreateResumeExecuting || (unprocessed && !timedOut) || false,
    timedOut: unprocessed && timedOut,
  }
}
